import { z } from 'zod';
export const NodeRoleSchema = z.enum(['world', 'world-light', 'infra', 'spare']);
export const WorldWeightSchema = z.enum(['heavy', 'light']);
export const NodeNameSchema = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9.-]{0,62}$/);
export const NodeRolesSchema = z.record(NodeNameSchema, NodeRoleSchema);
export const NodeWolSchema = z.record(
  NodeNameSchema,
  z
    .object({
      mac: z.string().regex(/^(?:[0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/),
      broadcast: z.ipv4(),
    })
    .strict(),
);
export const NodeNameParamsSchema = z.object({ name: NodeNameSchema }).strict();
export type NodeRole = z.infer<typeof NodeRoleSchema>;
export type WorldWeight = z.infer<typeof WorldWeightSchema>;
export type NodeRoles = z.infer<typeof NodeRolesSchema>;
export type NodeWol = z.infer<typeof NodeWolSchema>;
export interface NodeInfo {
  name: string;
  role?: NodeRole;
  online: boolean;
  memoryTotalMb: number;
  memoryAllocatedMb: number;
  /** 動いている VM だけの割り当て（短い間だけ使うテスト用 VM の空き判定に使う） */
  memoryRunningMb?: number;
}
export interface CapacityPolicy {
  maxNodeAllocationRatio: number;
  maxVmMemoryMb: number;
  currentHaTotalMb: number;
}
export const defaultCapacityPolicy = { maxNodeAllocationRatio: 0.8, maxVmMemoryMb: 30720 };
export function selectNode(
  nodes: NodeInfo[],
  request: { memoryMb: number; ha: boolean; weight?: WorldWeight; preferredNode?: string },
  policy: CapacityPolicy,
): NodeInfo {
  if (!Number.isInteger(request.memoryMb) || request.memoryMb <= 0) throw new Error('Invalid memory request');
  if (request.memoryMb > policy.maxVmMemoryMb) throw new Error('VM memory limit exceeded');
  const priority: NodeRole[] =
    request.weight === 'light' ? ['world-light', 'world', 'spare'] : ['world', 'spare', 'world-light'];
  const candidates = request.preferredNode ? nodes.filter((n) => n.name === request.preferredNode) : nodes;
  if (request.preferredNode && candidates.some((n) => n.role === 'infra'))
    throw new Error('Cannot place world on infra');
  const node = candidates
    .filter(
      (n) =>
        n.role !== 'infra' &&
        n.online &&
        n.memoryAllocatedMb + request.memoryMb <= n.memoryTotalMb * policy.maxNodeAllocationRatio,
    )
    .toSorted(
      (a, b) =>
        priority.indexOf(a.role ?? 'world') - priority.indexOf(b.role ?? 'world') ||
        b.memoryTotalMb - b.memoryAllocatedMb - (a.memoryTotalMb - a.memoryAllocatedMb) ||
        a.name.localeCompare(b.name),
    )[0];
  if (!node) throw new Error('No online node has sufficient memory within allocation limit');
  return node;
}
/** 最大の常時稼働ワールドノードを失った場合。非 HA VM の割当は維持する。 */
export function haCapacityWarnings(
  nodes: NodeInfo[],
  totalHaMb: number,
  policy = defaultCapacityPolicy,
  haByNode: Record<string, number> = {},
): string[] {
  const worlds = nodes.filter((n) => ['world', 'world-light'].includes(n.role ?? 'world'));
  const largest = worlds.toSorted((a, b) => b.memoryTotalMb - a.memoryTotalMb || a.name.localeCompare(b.name))[0];
  const survivors = nodes.filter((n) => n !== largest && n.role !== 'infra');
  const capacity = (n: NodeInfo) =>
    Math.max(
      0,
      n.memoryTotalMb * policy.maxNodeAllocationRatio - Math.max(0, n.memoryAllocatedMb - (haByNode[n.name] ?? 0)),
    );
  const onlineCapacity = survivors.filter((n) => n.online).reduce((sum, n) => sum + capacity(n), 0);
  if (totalHaMb <= onlineCapacity) return [];
  const spareCapacity = survivors
    .filter((n) => !n.online && n.role === 'spare')
    .reduce((sum, n) => sum + capacity(n), 0);
  return [
    '最大のサーバー用ノードが停止すると HA 対象のメモリを受けられません（必要 ' +
      totalHaMb +
      ' MB / 容量 ' +
      Math.floor(onlineCapacity) +
      ' MB）。' +
      (totalHaMb <= onlineCapacity + spareCapacity
        ? '予備ノードを起動すれば受けられます。'
        : '予備ノードを起動しても容量が不足します。'),
  ];
}

export const PanelCapacityEnvironmentSchema = z.object({
  MCPANEL_MAX_NODE_ALLOCATION_RATIO: z.coerce
    .number()
    .gt(0)
    .max(1)
    .default(defaultCapacityPolicy.maxNodeAllocationRatio),
  MCPANEL_MAX_VM_MEMORY_MB: z.coerce.number().int().positive().default(defaultCapacityPolicy.maxVmMemoryMb),
  MCPANEL_MAX_RUNNING_SERVERS: z.coerce.number().int().positive().default(5),
  MCPANEL_STORAGE_RESERVE_RATIO: z.coerce.number().min(0).lt(1).default(0.1),
});
export function capacityFromEnvironment(env: Record<string, string | undefined>) {
  const p = PanelCapacityEnvironmentSchema.parse(env);
  return {
    maxNodeAllocationRatio: p.MCPANEL_MAX_NODE_ALLOCATION_RATIO,
    maxVmMemoryMb: p.MCPANEL_MAX_VM_MEMORY_MB,
    maxRunningServers: p.MCPANEL_MAX_RUNNING_SERVERS,
    storageReserveRatio: p.MCPANEL_STORAGE_RESERVE_RATIO,
  };
}
export const TemplateVmidsSchema = z.record(NodeNameSchema, z.number().int().positive());
export const MoveNodeParamsSchema = z.object({ node: NodeNameSchema }).strict();
